import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { languages, Language } from '@/lib/i18n/translations-meta';
import { getRouteKeyFromPath, getHreflangUrls, getBlogPostHreflangUrls, getPrerenderPath, stripHtmlSuffix, RouteKey } from '@/lib/i18n/routes';
import { SITE_URL } from '@/config/siteConfig';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { getSectionMeta } from '@/lib/seo/sectionMeta';
import { buildCanonicalUrl } from '@/lib/seo/canonicalUrl';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  noindex?: boolean;
  /**
   * Marks the response as a soft-404. Suppresses the canonical link and
   * hreflang alternates (a 404 has no canonical URL and no localized twins)
   * and forces robots="noindex, follow" so crawlers still traverse outbound
   * links (home, nav) without indexing the error page or attributing the
   * bad URL to another page via canonical.
   */
  isNotFound?: boolean;
  ogType?: 'website' | 'article';
  ogImage?: string;
  articlePublishedTime?: string;
  articleAuthor?: string;
  /**
   * When true, the values passed in props always win over any admin-managed
   * DB override from `seo_meta`. Use on pages where component-level copy is
   * the source of truth (e.g. freshly rewritten landing pages).
   */
  preferProps?: boolean;
  /**
   * Blog posts only: translated slug per language (e.g. { de: '...' }).
   * Required for correct hreflang alternates, since each language has its own
   * slug. Without it every alternate would point at the current slug.
   */
  hreflangSlugs?: Partial<Record<Language, string>>;
}

export function SEOHead({ 
  title, 
  description, 
  canonical, 
  noindex = false,
  isNotFound = false,
  ogType = 'website',
  ogImage,
  articlePublishedTime,
  articleAuthor,
  preferProps = false,
  hreflangSlugs,
}: SEOHeadProps) {
  const location = useLocation();
  // Stable dep: the slug map is often rebuilt on each render.
  const hreflangSlugsKey = hreflangSlugs ? JSON.stringify(hreflangSlugs) : '';
  const { language } = useLanguage();
  
  // Determine route key (used for both hreflang and DB SEO override lookup)
  const routeKey = getRouteKeyFromPath(location.pathname);

  // Resolve SEO copy in priority order:
  //   1. DB override (admin-managed via `seo_meta`)
  //   2. Static catalog (`sectionMeta`) — unique per section × language
  //   3. Component-provided props (page-owned defaults)
  // `preferProps` lets a page force its own copy to win over both.
  const seoOverride = useSeoMeta(routeKey, language);
  const catalog = getSectionMeta(routeKey, language);
  const effectiveTitle = preferProps
    ? title
    : (seoOverride?.title || catalog?.title || title);
  const effectiveDescription = preferProps
    ? description
    : (seoOverride?.description || catalog?.description || description);
  const effectiveOgImage = preferProps ? ogImage : (seoOverride?.ogImage || ogImage);

  // Generate canonical URL based on current language path
  const normalizedPath = stripHtmlSuffix(location.pathname);
  const canonicalUrl = canonical || buildCanonicalUrl(normalizedPath);

  // Canonical + robots are written in a layout effect so they land in the head
  // BEFORE any sibling <JsonLdSchema> useEffect injects structured data.
  // Structured-data validators read <link rel="canonical"> to resolve the
  // expected @id/url of each node; if JSON-LD appeared first the check would
  // intermittently error on async pages (blog posts).
  useLayoutEffect(() => {
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (isNotFound) {
      if (existingCanonical) existingCanonical.remove();
    } else {
      let canonicalLink = existingCanonical;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonicalUrl);
    }

    // Robots: soft-404 = "noindex, follow" so crawlers still traverse nav
    // links; regular noindex pages stay "noindex, nofollow".
    let robotsMeta = document.querySelector('meta[name="robots"]');
    const ensureRobots = () => {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      return robotsMeta;
    };
    if (isNotFound) {
      ensureRobots().setAttribute('content', 'noindex, follow');
    } else if (noindex) {
      ensureRobots().setAttribute('content', 'noindex, nofollow');
    } else if (robotsMeta) {
      robotsMeta.setAttribute('content', 'index, follow');
    }
  }, [canonicalUrl, isNotFound, noindex]);

  useEffect(() => {

    // Update document title
    document.title = effectiveTitle;

    // Update html lang attribute dynamically
    const langCode = language === 'dk' ? 'da' : language;
    document.documentElement.lang = langCode;

    // Helper function to set or create meta tag
    const setMetaTag = (selector: string, attribute: string, value: string, content: string) => {
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Update meta description
    setMetaTag('meta[name="description"]', 'name', 'description', effectiveDescription);

    // Update Open Graph tags
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', effectiveTitle);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', effectiveDescription);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', ogType);
    
    if (effectiveOgImage) {
      setMetaTag('meta[property="og:image"]', 'property', 'og:image', effectiveOgImage);
    }

    // Article-specific Open Graph tags
    if (ogType === 'article') {
      if (articlePublishedTime) {
        setMetaTag('meta[property="article:published_time"]', 'property', 'article:published_time', articlePublishedTime);
      }
      if (articleAuthor) {
        setMetaTag('meta[property="article:author"]', 'property', 'article:author', articleAuthor);
      }
    }

    // Update Twitter tags
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', effectiveTitle);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', effectiveDescription);
    if (effectiveOgImage) {
      setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', effectiveOgImage);
    }

    // Hreflang / OG / title handling continues below. Canonical + robots are
    // written in the layout effect above so they always exist before any
    // sibling <JsonLdSchema> effect emits structured data.


    // Remove existing hreflang tags
    const existingHreflangs = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingHreflangs.forEach(el => el.remove());

    // Soft-404 has no localized twins — skip hreflang emission entirely.
    if (!isNotFound) {
      const blogPostMatch = normalizedPath.match(/^(?:\/[a-z]{2})?\/blog\/(.+)$/);
      let hreflangUrls: Record<string, string>;

      if (blogPostMatch) {
        const slug = blogPostMatch[1];
        hreflangUrls = getBlogPostHreflangUrls(slug, SITE_URL, hreflangSlugs);
      } else if (routeKey) {
        hreflangUrls = getHreflangUrls(routeKey, SITE_URL);
      } else {
        // Unknown route - fallback to same path for all languages
        hreflangUrls = {};
        languages.forEach((lang: Language) => {
          const hreflangCode = lang === 'dk' ? 'da' : lang;
          hreflangUrls[hreflangCode] = `${SITE_URL}${getPrerenderPath(normalizedPath)}`;
        });
        hreflangUrls['x-default'] = `${SITE_URL}${getPrerenderPath(normalizedPath)}`;
      }

      Object.entries(hreflangUrls).forEach(([lang, url]) => {
        const hreflangLink = document.createElement('link');
        hreflangLink.setAttribute('rel', 'alternate');
        hreflangLink.setAttribute('hreflang', lang);
        hreflangLink.setAttribute('href', url);
        document.head.appendChild(hreflangLink);
      });
    }

    return () => {
      // Cleanup hreflang tags on unmount
      const hreflangs = document.querySelectorAll('link[rel="alternate"][hreflang]');
      hreflangs.forEach(el => el.remove());
    };
  }, [effectiveTitle, effectiveDescription, canonicalUrl, noindex, isNotFound, ogType, effectiveOgImage, articlePublishedTime, articleAuthor, language, location.pathname, routeKey, hreflangSlugsKey]);

  return null;
}
